// GtLexaSession: one Lexa session, as the candidate sees it in their portal and
// as admins see it on the CRM. The recording on the left, the report and the
// transcript on the right. Every report line and transcript line that points
// at a moment in the call jumps the video there.
//
// Shared by the portal (admin=false) and the CRM (admin=true: also shows the
// model that ran, the cost, the credit, and Lexa's memory note).
import { useState, useRef, useEffect } from 'react';
import API_BASE from '../config';

export const TOPIC_LABEL = {
  cv: 'CV and positioning', next_move: 'Your next move', salary: 'Salary and offers',
  linkedin: 'LinkedIn and personal brand', confidence: 'Confidence and nerves', other: 'Something else',
};
const ENDED = {
  completed: 'Lexa closed the session', candidate_ended: 'Ended by the candidate', time_limit: 'Stopped at the time limit',
  dropped: 'The call dropped and was not rejoined', lexa_did_not_join: 'Lexa did not join: credit returned',
  dropped_early: 'Dropped in the first three minutes: credit returned', disconnected: 'The call dropped', never_started: 'Never started', discarded: 'Discarded',
};
export const MODEL_LABEL = { haiku: 'Claude Haiku 5.5', sol: 'GPT-6.1 Sol' };

// Fetches a PDF with the sign-in header and hands it to the browser to save.
export async function downloadPdf(getToken, path) {
  const t = await getToken();
  const r = await fetch(API_BASE + path, { headers: { Authorization: 'Bearer ' + t } });
  if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.message || 'Could not make the PDF.'); }
  const blob = await r.blob();
  const cd = r.headers.get('Content-Disposition') || '';
  const m = /filename="([^"]+)"/.exec(cd);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = (m && m[1]) || 'lexa-report.pdf';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function ukTime(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
export function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
export function mins(s) { const n = Math.round((Number(s) || 0) / 60); return n < 1 ? 'under a minute' : n + ' min'; }
export function statusOf(s) {
  if (s.status === 'completed') return s.report_status === 'done' ? { label: 'Report ready', tone: 'green' } : s.report_status === 'failed' ? { label: 'Report failed', tone: 'rose' } : { label: 'Writing report', tone: 'slate' };
  if (s.status === 'in_progress') return { label: 'In progress', tone: 'amber' };
  if (s.status === 'ready') return { label: 'Not started', tone: 'teal' };
  if (s.status === 'failed') return { label: s.is_test ? 'Did not work' : 'Credit returned', tone: 'grey' };
  return { label: s.status === 'expired' ? 'Expired' : s.status, tone: 'grey' };
}

// Where each transcript line starts in its session's recording (as on Nathan's review screen).
function cues(turns, recs) {
  const out = {};
  recs.forEach((x, i) => {
    const base = x.started_at ? new Date(x.started_at).getTime() : NaN;
    if (isNaN(base)) return;
    let prevEnd = base; let prevCut = false;
    turns.filter((t) => (t.session || 1) === x.session).forEach((t) => {
      const end = new Date(t.at).getTime();
      if (isNaN(end)) return;
      let st = t.start_at ? new Date(t.start_at).getTime() : end - (Math.max(1, t.words || 1) / 2.5) * 1000;
      if (!t.start_at && !prevCut && st < prevEnd) st = prevEnd;
      prevEnd = end; prevCut = !!t.interrupted;
      out[t.turn] = { rec: i, sec: Math.max(0, (st - base) / 1000 - 0.8) };
    });
  });
  return out;
}
function clockOf(sec) { const v = Math.max(0, Math.round(sec + 0.8)); return Math.floor(v / 60) + ':' + String(v % 60).padStart(2, '0'); }

function Jump({ turn, cueFor, onSeek }) {
  if (turn == null || !cueFor[turn]) return null;
  return (
    <button type="button" className="gtlx-jump" onClick={() => onSeek(turn)} title="Play this moment">
      <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>{clockOf(cueFor[turn].sec)}
    </button>
  );
}

export function ReportBody({ report, cueFor, onSeek, status, error }) {
  const cf = cueFor || {};
  const seek = onSeek || (() => {});
  if (!report) {
    return (
      <div className="gtlx-rwait">
        {status === 'failed' ? <><b>The report did not write</b><p>{error || 'Something went wrong.'}</p></> : <><span className="gtlx-orb gtlx-spin" aria-hidden="true" /><b>Lexa is writing the report</b><p>It usually takes about a minute after the call ends.</p></>}
      </div>
    );
  }
  if (report.short) return <div className="gtlx-rwait"><b>No report for this one</b><p>{report.summary}</p></div>;
  return (
    <div className="gtlx-report">
      {report.summary && <p className="gtlx-sum">{report.summary}</p>}
      {report.kind === 'interview' ? (
        <>
          {(report.fix_first || []).length > 0 && (
            <section className="gtlx-sec gtlx-sec--fix">
              <h4>Fix these first</h4>
              <ol>{report.fix_first.map((x, i) => <li key={i}>{x}</li>)}</ol>
            </section>
          )}
          {(report.questions || []).length > 0 && (
            <section className="gtlx-sec">
              <h4>Question by question</h4>
              {report.questions.map((q, i) => (
                <article key={i} className="gtlx-q">
                  <div className="gtlx-q-h"><span className="gtlx-num">{i + 1}</span><b>{q.question}</b><Jump turn={q.turn} cueFor={cf} onSeek={seek} /></div>
                  {q.quote && <blockquote className="gtlx-quote">“{q.quote}”<Jump turn={q.answer_turn} cueFor={cf} onSeek={seek} /></blockquote>}
                  <div className="gtlx-q-g">
                    {q.worked && <div className="gtlx-q-w"><span>What worked</span><p>{q.worked}</p></div>}
                    {q.fix && <div className="gtlx-q-f"><span>What to tighten</span><p>{q.fix}</p></div>}
                  </div>
                  {q.stronger && <div className="gtlx-q-s"><span>A stronger way to say it</span><p>{q.stronger}</p></div>}
                </article>
              ))}
            </section>
          )}
          {(report.still_to_prepare || []).length > 0 && (
            <section className="gtlx-sec"><h4>Still to prepare for this role</h4><ul>{report.still_to_prepare.map((x, i) => <li key={i}>{x}</li>)}</ul></section>
          )}
          {report.next_session && <section className="gtlx-sec gtlx-sec--next"><h4>Next session</h4><p>{report.next_session}</p></section>}
        </>
      ) : (
        <>
          {report.came_for && <section className="gtlx-sec"><h4>What you came for</h4><p>{report.came_for}</p></section>}
          {(report.action_plan || []).length > 0 && (
            <section className="gtlx-sec gtlx-sec--fix">
              <h4>Your action plan</h4>
              <ol>{report.action_plan.map((x, i) => <li key={i}><b>{x.step}</b>{x.detail ? <span> {x.detail}</span> : null}<Jump turn={x.turn} cueFor={cf} onSeek={seek} /></li>)}</ol>
            </section>
          )}
          {(report.discussed || []).length > 0 && (
            <section className="gtlx-sec"><h4>What you talked about</h4><ul>{report.discussed.map((x, i) => <li key={i}>{x.point}<Jump turn={x.turn} cueFor={cf} onSeek={seek} /></li>)}</ul></section>
          )}
          {(report.wording || []).length > 0 && (
            <section className="gtlx-sec">
              <h4>Wording to use</h4>
              {report.wording.map((w, i) => (
                <div key={i} className="gtlx-word"><span>{w.label || 'Suggested wording'}<Jump turn={w.turn} cueFor={cf} onSeek={seek} /></span><p>{w.text}</p></div>
              ))}
            </section>
          )}
          {(report.bring_next_time || []).length > 0 && <section className="gtlx-sec gtlx-sec--next"><h4>Before next time</h4><ul>{report.bring_next_time.map((x, i) => <li key={i}>{x}</li>)}</ul></section>}
        </>
      )}
      {report.written_at && <p className="gtlx-fine">Written by Lexa {ukTime(report.written_at)}. Coaching, not an assessment.</p>}
    </div>
  );
}

export default function GtLexaSession({ d, admin, memory, busy, onRegenerate, youLabel }) {
  const [tab, setTab] = useState('report');
  const [rec, setRec] = useState(0);
  const [nowSec, setNowSec] = useState(0);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const txRef = useRef(null);
  const pendingSeek = useRef(null);
  const turns = d.transcript || [];
  const recs = (d.recordings || []).filter((x) => x.url);
  const r = recs[rec] || recs[0];
  const curRec = recs.indexOf(r);
  const cueFor = cues(turns, recs);
  let activeTurn = null;
  turns.forEach((t) => { const c = cueFor[t.turn]; if (c && c.rec === curRec && c.sec <= nowSec + 0.25) activeTurn = t.turn; });
  useEffect(() => { setNowSec(0); setPlaying(false); }, [rec]);
  useEffect(() => {
    if (!playing || tab !== 'transcript' || activeTurn == null || !txRef.current) return;
    const el = txRef.current.querySelector('[data-turn="' + activeTurn + '"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeTurn, playing, tab]);
  function seek(turn) {
    const c = cueFor[turn];
    if (!c) return;
    if (c.rec !== curRec) { pendingSeek.current = c.sec; setRec(c.rec); return; }
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = c.sec;
    v.play().catch(() => {});
  }
  function onMeta() {
    const v = videoRef.current;
    if (v && pendingSeek.current != null) { v.currentTime = pendingSeek.current; pendingSeek.current = null; v.play().catch(() => {}); }
  }
  const you = youLabel || 'You';
  const ran = d.model_ran && Array.isArray(d.model_ran.ran) ? d.model_ran.ran : [];

  return (
    <div className="gtlx-view">
      <div className="gtlx-view-l">
        <div className="gtlx-media">
          {r ? (
            <video key={r.url} ref={videoRef} className="gtlx-video" src={API_BASE + r.url} controls preload="metadata" playsInline
              onLoadedMetadata={onMeta} onTimeUpdate={(e) => setNowSec(e.currentTarget.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}>
              <track kind="captions" />
            </video>
          ) : (
            <div className="gtlx-novideo">{d.status === 'ready' ? 'Not started yet.' : 'No recording. The browser may not support recording, or it is still uploading.'}</div>
          )}
          {recs.length > 1 && (
            <div className="gtlx-parts">
              {recs.map((x, i) => <button key={x.n} type="button" className={'gtlx-part' + (i === rec ? ' gtlx-part--on' : '')} onClick={() => setRec(i)}>Part {x.session}</button>)}
            </div>
          )}
        </div>
        <div className="gtlx-facts">
          <span><b>{mins(d.duration_seconds)}</b> talking</span>
          {d.kind === 'interview' && <span>{d.mode === 'real' ? 'Real mode' : 'Practice mode'}</span>}
          {d.sessions > 1 && <span>{d.sessions} parts</span>}
          {d.ended_reason && <span>{ENDED[d.ended_reason] || d.ended_reason}</span>}
          {admin && <span>{MODEL_LABEL[d.model] || d.model}{d.model_ran && d.model_ran.fell_back ? ', fell back to Claude Haiku 5.5' : ''}</span>}
          {admin && ran.length > 0 && <span>Ran on {ran.join(', ')}</span>}
          {admin && d.cost_pence != null && <span>Model cost {(Number(d.cost_pence)).toFixed(1)}p</span>}
          {admin && !d.is_test && <span>{d.credit_returned ? 'Credit returned' : d.charged ? 'Used one credit' : 'No credit used'}</span>}
        </div>
        {d.goal && <div className="gtlx-goal"><span>{admin ? 'What they wanted' : 'What you wanted'}</span><p>{d.goal}</p></div>}
        {admin && memory !== undefined && (
          <div className="gtlx-mem">
            <span>Lexa's memory of them</span>
            <p>{memory && memory.note ? memory.note : 'Nothing yet. Written after their first full session.'}</p>
          </div>
        )}
      </div>
      <div className="gtlx-view-r">
        <div className="gtlx-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'report'} className={'gtlx-tab' + (tab === 'report' ? ' gtlx-tab--on' : '')} onClick={() => setTab('report')}>Report</button>
          <button type="button" role="tab" aria-selected={tab === 'transcript'} className={'gtlx-tab' + (tab === 'transcript' ? ' gtlx-tab--on' : '')} onClick={() => setTab('transcript')}>Transcript</button>
          {admin && d.status === 'completed' && onRegenerate && (
            <button type="button" className="gtlx-tabact" disabled={!!busy} onClick={onRegenerate}>{busy ? 'Writing…' : 'Write the report again'}</button>
          )}
        </div>
        {tab === 'report' ? (
          <ReportBody report={d.report} cueFor={cueFor} onSeek={seek} status={d.report_status} error={d.report_error} />
        ) : (
          <div className="gtlx-tx" ref={txRef}>
            {turns.length === 0 ? <p className="gtlx-muted">Nothing said yet.</p> : turns.map((t) => (
              <div key={t.turn} data-turn={t.turn}
                className={'gtlx-turn gtlx-turn--' + t.speaker + (cueFor[t.turn] ? ' gtlx-turn--seek' : '') + (activeTurn === t.turn ? ' gtlx-turn--now' : '')}
                role={cueFor[t.turn] ? 'button' : undefined} tabIndex={cueFor[t.turn] ? 0 : undefined}
                onClick={cueFor[t.turn] ? () => seek(t.turn) : undefined}
                onKeyDown={cueFor[t.turn] ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); seek(t.turn); } } : undefined}>
                <div className="gtlx-turn-h"><b>{t.speaker === 'lexa' ? 'Lexa' : you}</b><time>{cueFor[t.turn] ? clockOf(cueFor[t.turn].sec) : ''}</time></div>
                <p>{t.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
